import { Loader2Icon } from "lucide-react";
import { useEffect, useId, useState } from "react";

import type { RemovalStepLabel } from "../sidebar/removalStepLabels";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { matchesEnvironmentConfirmation } from "./deleteEnvironmentConfirm";

const DELETE_ENVIRONMENT_CONFIRM_BUTTON = "Delete environment";

/**
 * The in-flight region: which step is running, and that waiting is expected.
 *
 * Individual project removals have durable receipts, but this client controls
 * the sequence and the final Environment deletion. Keep the progress visible
 * while the current deletion is running.
 */
function DeleteEnvironmentProgress({ stepLabel }: { readonly stepLabel: RemovalStepLabel | null }) {
  return (
    <div
      aria-live="polite"
      className="mt-3 flex items-start gap-2 rounded-md border border-border/60 bg-secondary/40 p-2.5 text-[11px] text-muted-foreground"
      data-slot="delete-environment-progress"
      role="status"
    >
      <Loader2Icon aria-hidden="true" className="mt-px size-3 shrink-0 animate-spin" />
      <span>{stepLabel ?? "Starting"}. Keep Forge open until this finishes.</span>
    </div>
  );
}

/**
 * "Delete environment" confirmation, with a type-to-confirm gate.
 *
 * A separate dialog from the sidebar's `SidebarRemoveProjectDialog` rather
 * than a flag on it. The two say genuinely different things: removing a
 * project from the sidebar is about a project leaving your views, while this
 * destroys a running machine — and the consequence that matters here, that
 * uncommitted work on that machine is gone for good, has no sidebar
 * equivalent. Adding a `confirmPhrase` prop to the shared dialog would have
 * put a speed bump on the sidebar's flow too, which nobody asked for.
 */
export function DeleteEnvironmentDialog({
  open,
  onOpenChange,
  environmentName,
  hasRunningSprite,
  hasProjects,
  isDeleting,
  stepLabel,
  onConfirmDelete,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly environmentName: string;
  /** Whether a Sprite exists to destroy, which decides the work-loss warning. */
  readonly hasRunningSprite: boolean;
  readonly hasProjects: boolean;
  readonly isDeleting: boolean;
  readonly stepLabel: RemovalStepLabel | null;
  readonly onConfirmDelete: () => void;
}) {
  const inputId = useId();
  const [typed, setTyped] = useState("");
  const confirmed = matchesEnvironmentConfirmation(typed, environmentName);

  // Each opening starts from an empty box. A retained value would let a second
  // delete be confirmed by a phrase typed for the first one — and the rows on
  // this page look alike, which is the whole reason the gate is here.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  return (
    <AlertDialog
      open={open}
      // Base UI's AlertDialog blocks outside-press but still wires Escape.
      // Escape mid-delete tore down the only surface reporting it, leaving a
      // destructive operation running with nothing on screen.
      onOpenChange={(next) => {
        if (!next && isDeleting) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogPopup className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{environmentName}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            This deletes the Environment, its backing Sprite, and its Forge Checkout registrations.
          </AlertDialogDescription>
          <p className="mt-3 rounded-md border border-destructive-border bg-destructive-surface p-2.5 text-[13px] leading-[1.45] text-destructive-foreground">
            {hasRunningSprite
              ? "Active work stops. Uncommitted files on the Sprite may be unrecoverable."
              : "No Sprite has been created yet. Deleting still removes the Environment and its Forge Checkout registrations."}
          </p>
          {hasProjects ? (
            <p className="mt-3 rounded-md border border-border/60 bg-secondary/30 p-2.5 text-xs text-muted-foreground">
              Connected CMMD projects stay in CMMD. Their Forge Checkout registrations are removed.
            </p>
          ) : null}
          {isDeleting ? <DeleteEnvironmentProgress stepLabel={stepLabel} /> : null}
        </AlertDialogHeader>
        <div className="grid gap-1.5">
          <Label htmlFor={inputId}>
            Type <span className="font-medium text-foreground">{environmentName}</span> to confirm
          </Label>
          <Input
            id={inputId}
            autoComplete="off"
            disabled={isDeleting}
            placeholder={environmentName}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" disabled={isDeleting} />}>
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            disabled={isDeleting || !confirmed}
            onClick={onConfirmDelete}
          >
            {isDeleting ? "Deleting..." : DELETE_ENVIRONMENT_CONFIRM_BUTTON}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
